import React from 'react';
import { lotAccessLegendItems, mapLegendItems } from './mapLegendData';

const pageTitleColor = '#28445a';
const pageBorderColor = '#d4d7dc';

const pageStyles = {
    landscape: {
        width: '1224px',
        height: '792px',
        padding: '36px',
    },
    portrait: {
        width: '612px',
        height: '792px',
        padding: '30px 36px 28px 36px',
    },
};

const layoutStyles = {
    page: {
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        color: '#1f2937',
        fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
        position: 'relative',
        overflow: 'hidden',
    },
    title: {
        color: pageTitleColor,
        fontSize: '28px',
        fontWeight: 700,
        marginBottom: '6px',
    },
    subtitle: {
        color: '#4b5563',
        fontSize: '14px',
        marginBottom: '20px',
    },
    sectionTitle: {
        color: pageTitleColor,
        fontSize: '14px',
        fontWeight: 700,
        marginBottom: '6px',
    },
    sectionSubtitle: {
        color: '#6b7280',
        fontSize: '12px',
        marginBottom: '18px',
    },
    footer: {
        position: 'absolute',
        right: '36px',
        bottom: '24px',
        color: '#4b5563',
        fontSize: '12px',
    },
};

function chunkItems(items, itemsPerRow) {
    const rows = [];

    for (let index = 0; index < items.length; index += itemsPerRow) {
        rows.push(items.slice(index, index + itemsPerRow));
    }

    return rows;
}

function formatDate(dateValue) {
    if (!dateValue) {
        return '';
    }

    const date = new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
        return dateValue;
    }

    return new Intl.DateTimeFormat('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(date);
}

function getMapSubtitle(site, revision) {
    const subtitleParts = [];

    if (site?.county) {
        subtitleParts.push(`${site.county} County`);
    }

    if (revision?.revisionNumber !== undefined) {
        subtitleParts.push(`Revision ${revision.revisionNumber}`);
    }

    return subtitleParts.join(' | ');
}

function LegendItem({ item }) {
    if (item.textClassName === 'trackoutControlDevice') {
        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', lineHeight: 1.05, whiteSpace: 'nowrap' }}>
                <svg
                    width="15"
                    height="15"
                    viewBox="0 0 15 15"
                    style={{ flexShrink: 0, overflow: 'visible' }}
                    aria-hidden="true"
                >
                    <circle cx="7.5" cy="7.5" r="6.5" fill="none" stroke="#9ca3af" strokeWidth="1" />
                    <text
                        x="7.5"
                        y="7.5"
                        textAnchor="middle"
                        dominantBaseline="central"
                        fontSize="9"
                        fontWeight="700"
                        fill="#111827"
                        fontFamily='"Segoe UI", Tahoma, Geneva, Verdana, sans-serif'
                    >
                        T
                    </text>
                </svg>
                <span style={{ whiteSpace: 'nowrap' }}>{item.text}</span>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', lineHeight: 1.05, whiteSpace: 'nowrap' }}>
            <i className={`${item.icon} ${item.color}`} style={{ fontSize: '12px', minWidth: '14px', flexShrink: 0 }} />
            <span style={{ whiteSpace: 'nowrap' }}>{item.text}</span>
        </div>
    );
}

function RevisionRows({ revisions }) {
    return revisions.map((mapRevision, index) => (
        <tr key={`${mapRevision.revisionId ?? index}-${mapRevision.revisionNumber ?? index}`}>
            <td style={tableBodyCellStyles}>{mapRevision.revisionNumber ?? ''}</td>
            <td style={tableBodyCellStyles}>{formatDate(mapRevision.createdAt)}</td>
            <td style={{ ...tableBodyCellStyles, whiteSpace: 'normal' }}>{mapRevision.revisionDetails ?? ''}</td>
        </tr>
    ));
}

const tableHeaderCellStyles = {
    borderBottom: `1px solid ${pageBorderColor}`,
    color: pageTitleColor,
    fontSize: '8px',
    fontWeight: 700,
    padding: '6px 5px',
    textAlign: 'left',
};

const tableBodyCellStyles = {
    borderBottom: `1px solid ${pageBorderColor}`,
    fontSize: '8px',
    padding: '6px 5px',
    verticalAlign: 'top',
    lineHeight: 1.15,
};

export default function MapPdfDocument({ map, mapImageUrl, revision, site }) {
    // const visibleLegendItems = mapLegendItems.filter(item => item.text);
    const visibleLegendItems = mapLegendItems;
    const visibleLotAccessLegendItems = lotAccessLegendItems.filter(item => item.text);
    const legendRows = chunkItems(visibleLegendItems, 4);
    const lotAccessRows = chunkItems(visibleLotAccessLegendItems, 3);
    const orderedRevisions = [...(map?.revisions ?? [])]
        .sort((leftRevision, rightRevision) => (leftRevision?.revisionId ?? 0) - (rightRevision?.revisionId ?? 0));
    const siteTitle = site?.siteName || map?.description || 'Stormwater Map';
    const mapTitle = map?.description || revision?.mapFileName || 'Site Map';
    const mapSubtitle = getMapSubtitle(site, revision);

    return (
        <>
            <div
                data-map-pdf-page="1"
                data-page-width="1224"
                data-page-height="792"
                style={{ ...layoutStyles.page, ...pageStyles.landscape }}
            >
                <div style={layoutStyles.title}>{siteTitle}</div>
                <div style={layoutStyles.subtitle}>{mapTitle}</div>
                <div style={{ ...layoutStyles.sectionSubtitle, marginBottom: '18px' }}>{mapSubtitle}</div>

                <div
                    style={{
                        border: `1px solid ${pageBorderColor}`,
                        borderRadius: '10px',
                        padding: '18px',
                        height: '620px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fbfbfc',
                    }}
                >
                    <img
                        alt={mapTitle}
                        src={mapImageUrl}
                        style={{
                            maxWidth: '100%',
                            maxHeight: '100%',
                            objectFit: 'contain',
                        }}
                    />
                </div>

                <div style={layoutStyles.footer}>1 of 3</div>
            </div>

            <div
                data-map-pdf-page="2"
                data-page-width="612"
                data-page-height="792"
                style={{ ...layoutStyles.page, ...pageStyles.portrait }}
            >
                <div style={{ ...layoutStyles.sectionTitle, fontSize: '12px', marginBottom: '12px' }}>{mapTitle}</div>

                <table
                    style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        tableLayout: 'fixed',
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ ...tableHeaderCellStyles, width: '65px' }}>Update #</th>
                            <th style={{ ...tableHeaderCellStyles, width: '65px' }}>Date</th>
                            <th style={tableHeaderCellStyles}>Details</th>
                        </tr>
                    </thead>
                    <tbody>
                        <RevisionRows revisions={orderedRevisions} />
                    </tbody>
                </table>

                <div style={layoutStyles.footer}>2 of 3</div>
            </div>

            <div
                data-map-pdf-page="3"
                data-page-width="612"
                data-page-height="792"
                style={{ ...layoutStyles.page, ...pageStyles.portrait }}
            >
                <div style={{ color: '#111827', fontSize: '10px', fontWeight: 700, marginBottom: '12px' }}>LEGEND</div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '6px',
                        fontSize: '8px',
                        marginBottom: '22px',
                    }}
                >
                    {legendRows.map((row, rowIndex) => (
                        <div
                            key={`legend-row-${rowIndex}`}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '132px 126px 140px 1fr',
                                columnGap: '2px',
                                alignItems: 'center',
                                minHeight: '16px',
                            }}
                        >
                            {row.map(item => (
                                <LegendItem key={item.text} item={item} />
                            ))}
                            {Array.from({ length: 4 - row.length }, (_, emptyIndex) => (
                                <div key={`legend-empty-${rowIndex}-${emptyIndex}`} />
                            ))}
                        </div>
                    ))}
                </div>

                <div style={{ color: '#111827', fontSize: '10px', fontWeight: 700, marginBottom: '10px' }}>
                    Lot Access Modifications
                </div>
                <div style={{ color: '#111827', fontSize: '8px', lineHeight: 1.35, marginBottom: '6px' }}>
                    These are used when the lot is started and placed where the proposed driveway is to be installed to
                    allow for lot access. Example: Silt Fence is removed for the driveway to allow work on the lot and
                    a different protection is installed in its place.
                </div>
                <div style={{ color: '#111827', fontSize: '8px', lineHeight: 1.35, marginBottom: '10px' }}>
                    Installation date is correlated to the lot start date.
                </div>

                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '8px',
                        fontSize: '8px',
                    }}
                >
                    {lotAccessRows.map((row, rowIndex) => (
                        <div
                            key={`lot-access-row-${rowIndex}`}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr 1fr',
                                columnGap: '18px',
                                alignItems: 'center',
                                minHeight: '16px',
                            }}
                        >
                            {row.map(item => (
                                <LegendItem key={item.text} item={item} />
                            ))}
                            {Array.from({ length: 3 - row.length }, (_, emptyIndex) => (
                                <div key={`lot-access-empty-${rowIndex}-${emptyIndex}`} />
                            ))}
                        </div>
                    ))}
                </div>

                <div style={layoutStyles.footer}>3 of 3</div>
            </div>
        </>
    );
}
